import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { trpc, trpcClient } from '@/lib/trpc'
import { ArrowRight, CheckCircle2, Circle, Loader2, Play, RefreshCcw, Trash2 } from 'lucide-react'
import { ScreenataStepLayout } from '@/components/screenata/ScreenataStepLayout'
import { ScanFindingsFeed, ScanProgressBar } from '@/components/screenata/ScanFindingsFeed'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { toast } from 'sonner'
import { useEffect, useMemo, useState, type FC } from 'react'

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/screenata/integrations',
)({
  component: ScreenataIntegrationsPage,
})

type Provider = {
  id: CloudProvider
  name: string
  description: string
  icon: FC<{ className?: string }>
}

type CloudProvider = 'aws' | 'gcp' | 'azure'

type CloudConnectionInput = {
  provider: 'aws'
  accessKeyId: string
  secretAccessKey: string
  region?: string
}

const AWSLogo: FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6.763 10.036C6.763 10.358 6.795 10.618 6.852 10.806C6.917 10.994 6.998 11.198 7.111 11.418C7.159 11.498 7.175 11.578 7.175 11.65C7.175 11.754 7.111 11.858 6.974 11.962L6.499 12.277C6.402 12.341 6.306 12.373 6.218 12.373C6.106 12.373 5.994 12.317 5.882 12.213C5.769 12.101 5.673 11.973 5.593 11.829C5.513 11.677 5.433 11.509 5.345 11.309C4.757 12.013 4.008 12.365 3.098 12.365C2.444 12.365 1.926 12.181 1.554 11.813C1.182 11.445 0.992 10.958 0.992 10.35C0.992 9.702 1.214 9.175 1.666 8.775C2.118 8.375 2.716 8.175 3.468 8.175C3.728 8.175 3.996 8.199 4.28 8.239C4.564 8.279 4.856 8.343 5.164 8.415V7.903C5.164 7.327 5.044 6.919 4.812 6.671C4.572 6.423 4.168 6.303 3.588 6.303C3.316 6.303 3.036 6.335 2.748 6.407C2.46 6.479 2.18 6.567 1.908 6.679C1.796 6.727 1.708 6.759 1.652 6.775C1.596 6.791 1.556 6.799 1.524 6.799C1.396 6.799 1.332 6.711 1.332 6.527V6.167C1.332 6.023 1.356 5.919 1.412 5.855C1.468 5.791 1.564 5.727 1.708 5.663C1.98 5.527 2.3 5.415 2.668 5.327C3.036 5.231 3.428 5.187 3.844 5.187C4.732 5.187 5.385 5.391 5.813 5.799C6.233 6.207 6.449 6.831 6.449 7.671V10.036H6.763ZM3.428 11.309C3.68 11.309 3.94 11.261 4.212 11.165C4.484 11.069 4.724 10.894 4.924 10.654C5.044 10.51 5.132 10.35 5.172 10.166C5.212 9.982 5.244 9.766 5.244 9.518V9.23C5.02 9.174 4.78 9.126 4.532 9.094C4.284 9.062 4.044 9.046 3.812 9.046C3.316 9.046 2.948 9.142 2.692 9.342C2.436 9.542 2.312 9.822 2.312 10.19C2.312 10.534 2.404 10.79 2.596 10.966C2.78 11.197 3.06 11.309 3.428 11.309ZM9.429 12.277C9.269 12.277 9.165 12.253 9.101 12.197C9.037 12.149 8.981 12.037 8.933 11.877L7.243 5.919C7.195 5.751 7.171 5.639 7.171 5.575C7.171 5.439 7.243 5.367 7.387 5.367H8.083C8.251 5.367 8.363 5.391 8.419 5.447C8.483 5.495 8.531 5.607 8.579 5.767L9.789 10.654L10.903 5.767C10.943 5.599 10.991 5.495 11.055 5.447C11.119 5.399 11.239 5.367 11.399 5.367H11.967C12.135 5.367 12.247 5.391 12.311 5.447C12.375 5.495 12.431 5.607 12.463 5.767L13.593 10.718L14.835 5.767C14.883 5.599 14.939 5.495 14.995 5.447C15.059 5.399 15.171 5.367 15.331 5.367H15.995C16.139 5.367 16.219 5.431 16.219 5.575C16.219 5.615 16.211 5.655 16.203 5.703C16.195 5.751 16.179 5.815 16.147 5.903L14.409 11.861C14.361 12.029 14.305 12.133 14.241 12.181C14.177 12.229 14.065 12.261 13.913 12.261H13.305C13.137 12.261 13.025 12.237 12.961 12.181C12.897 12.125 12.841 12.021 12.809 11.853L11.703 7.095L10.605 11.845C10.565 12.013 10.509 12.117 10.445 12.173C10.381 12.229 10.261 12.261 10.101 12.261H9.429V12.277ZM19.268 12.405C18.844 12.405 18.42 12.357 18.012 12.261C17.604 12.165 17.284 12.061 17.068 11.941C16.932 11.861 16.844 11.773 16.812 11.693C16.78 11.613 16.764 11.525 16.764 11.445V11.069C16.764 10.885 16.836 10.797 16.972 10.797C17.02 10.797 17.068 10.805 17.116 10.821C17.164 10.837 17.236 10.869 17.316 10.901C17.58 11.021 17.868 11.117 18.172 11.181C18.484 11.245 18.788 11.277 19.1 11.277C19.596 11.277 19.98 11.189 20.244 11.013C20.508 10.837 20.644 10.582 20.644 10.254C20.644 10.03 20.572 9.846 20.428 9.694C20.284 9.542 20.012 9.406 19.62 9.278L18.5 8.934C17.908 8.75 17.468 8.478 17.196 8.118C16.924 7.766 16.784 7.367 16.784 6.935C16.784 6.583 16.86 6.271 17.012 5.999C17.164 5.727 17.372 5.495 17.628 5.303C17.884 5.103 18.18 4.959 18.524 4.855C18.868 4.751 19.228 4.703 19.612 4.703C19.804 4.703 20.004 4.711 20.196 4.735C20.396 4.759 20.58 4.791 20.764 4.831C20.94 4.879 21.108 4.927 21.268 4.983C21.428 5.039 21.548 5.095 21.636 5.151C21.756 5.223 21.844 5.295 21.892 5.375C21.94 5.447 21.964 5.551 21.964 5.687V6.031C21.964 6.215 21.892 6.311 21.756 6.311C21.692 6.311 21.588 6.279 21.452 6.215C21.012 6.015 20.524 5.919 19.988 5.919C19.54 5.919 19.188 5.991 18.948 6.143C18.708 6.295 18.584 6.527 18.584 6.847C18.584 7.071 18.664 7.263 18.824 7.415C18.984 7.567 19.276 7.719 19.692 7.855L20.788 8.199C21.372 8.383 21.796 8.639 22.052 8.967C22.308 9.295 22.432 9.678 22.432 10.11C22.432 10.47 22.356 10.798 22.212 11.085C22.06 11.373 21.852 11.621 21.588 11.821C21.324 12.029 21.004 12.181 20.636 12.285C20.252 12.365 19.812 12.405 19.268 12.405Z" fill="#252F3E"/>
    <path d="M20.516 16.413C18.38 17.971 15.316 18.791 12.684 18.791C8.988 18.791 5.66 17.459 3.148 15.211C2.948 15.027 3.132 14.779 3.372 14.923C6.068 16.481 9.38 17.427 12.804 17.427C15.116 17.427 17.652 16.955 19.98 15.979C20.324 15.827 20.62 16.197 20.516 16.413Z" fill="#FF9900"/>
    <path d="M21.292 15.523C21.028 15.179 19.524 15.363 18.844 15.443C18.636 15.467 18.604 15.283 18.788 15.155C19.988 14.323 21.924 14.563 22.148 14.843C22.372 15.131 22.084 17.075 20.956 18.011C20.788 18.155 20.628 18.075 20.7 17.891C20.924 17.323 21.556 15.859 21.292 15.523Z" fill="#FF9900"/>
  </svg>
)

const GCPLogo: FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12.19 7.38L14.38 5.19L14.5 4.5C11.84 2.04 7.73 2.23 5.27 4.89C4.25 5.99 3.6 7.4 3.4 8.89L4 8.81L8.39 8.09L8.69 7.78C9.83 6.55 11.62 6.37 12.95 7.24L12.19 7.38Z" fill="#EA4335"/>
    <path d="M20.12 8.89C19.76 7.53 19.05 6.29 18.05 5.31L14.5 8.86C15.52 9.69 16.11 10.93 16.11 12.24V12.87C17.57 12.87 18.74 14.05 18.74 15.5C18.74 16.96 17.57 18.13 16.11 18.13H12.24L11.61 18.76V21.38L12.24 22H16.11C19.67 22 22.56 19.09 22.56 15.53C22.56 12.79 20.86 10.32 18.4 9.3L20.12 8.89Z" fill="#4285F4"/>
    <path d="M8.37 22H12.24V18.13H8.37C7.97 18.13 7.58 18.04 7.23 17.86L6.6 18.04L4.4 20.23L4.24 20.86C5.39 21.61 6.84 22 8.37 22Z" fill="#34A853"/>
    <path d="M8.37 9C4.81 9 1.92 11.91 1.92 15.47C1.92 17.28 2.66 18.95 3.92 20.14L7.47 16.59C6.74 16.22 6.24 15.47 6.24 14.62C6.24 13.4 7.23 12.38 8.49 12.38C9.34 12.38 10.09 12.88 10.46 13.61L14 10.06C12.82 8.81 11.15 9.05 9.34 9.05C9.02 9.02 8.7 9 8.37 9Z" fill="#FBBC05"/>
  </svg>
)

const AzureLogo: FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M13.05 4.24L6.56 18.05L2 18.2L7.09 4.24H13.05ZM14.78 6.09L22 19.76H16.12L14.05 15.2H8.53L14.78 6.09Z" fill="#0089D6"/>
  </svg>
)

const PROVIDERS: Provider[] = [
  {
    id: 'aws',
    name: 'AWS',
    description: 'Scan IAM roles, S3 bucket policies, CloudTrail logs, and security groups.',
    icon: AWSLogo,
  },
  {
    id: 'gcp',
    name: 'Google Cloud',
    description: 'Scan IAM bindings, Cloud Audit Logs, and organization policies.',
    icon: GCPLogo,
  },
  {
    id: 'azure',
    name: 'Azure',
    description: 'Scan role assignments, activity logs, and network security groups.',
    icon: AzureLogo,
  },
]

type CloudConnection = {
  provider: CloudProvider
  connected: boolean
  integrationId: string | null
  hasConfig: boolean
  configSummary: Record<string, unknown>
  lastTestedAt: string | null
  lastTestStatus: string | null
  lastScanAt: string | null
  lastScanSummary: Record<string, unknown> | null
}

type CloudConfigFormState = {
  awsAccessKeyId: string
  awsSecretAccessKey: string
  awsRegion: string
}

const EMPTY_CLOUD_FORM: CloudConfigFormState = {
  awsAccessKeyId: '',
  awsSecretAccessKey: '',
  awsRegion: 'us-east-1',
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null
}

function asSummary(summary: Record<string, unknown> | null | undefined): {
  pass: number
  warn: number
  fail: number
  error: number
} | null {
  if (!summary) return null
  const pass = typeof summary.pass === 'number' ? summary.pass : null
  const warn = typeof summary.warn === 'number' ? summary.warn : null
  const fail = typeof summary.fail === 'number' ? summary.fail : null
  const error = typeof summary.error === 'number' ? summary.error : null
  if (pass === null || warn === null || fail === null || error === null) {
    return null
  }
  return { pass, warn, fail, error }
}

function formatDate(value: string | null): string | null {
  if (!value) return null
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed.toLocaleString()
}

function toErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message
  return fallback
}

function toIntegrationType(provider: Exclude<CloudProvider, 'aws'>): 'GCP_READONLY' | 'AZURE_READONLY' {
  return provider === 'gcp' ? 'GCP_READONLY' : 'AZURE_READONLY'
}

function CloudConnectionDialog({
  provider,
  open,
  isSaving,
  isOAuthConnecting,
  initialSummary,
  onOpenChange,
  onSave,
  onConnectOAuth,
}: {
  provider: Provider | null
  open: boolean
  isSaving: boolean
  isOAuthConnecting: boolean
  initialSummary: Record<string, unknown>
  onOpenChange: (open: boolean) => void
  onSave: (input: CloudConnectionInput) => void
  onConnectOAuth: (provider: Exclude<CloudProvider, 'aws'>) => void
}) {
  const [form, setForm] = useState<CloudConfigFormState>(EMPTY_CLOUD_FORM)

  useEffect(() => {
    if (!open) return
    setForm({
      ...EMPTY_CLOUD_FORM,
      awsRegion: asString(initialSummary.region) ?? 'us-east-1',
    })
  }, [initialSummary, open, provider?.id])

  if (!provider) return null

  const handleSubmit = () => {
    if (provider.id === 'aws') {
      if (!form.awsAccessKeyId.trim() || !form.awsSecretAccessKey.trim()) {
        toast.error('AWS access key and secret are required')
        return
      }
      onSave({
        provider: 'aws',
        accessKeyId: form.awsAccessKeyId.trim(),
        secretAccessKey: form.awsSecretAccessKey.trim(),
        region: form.awsRegion.trim() || 'us-east-1',
      })
      return
    }
    onConnectOAuth(provider.id)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>Connect {provider.name}</DialogTitle>
          <DialogDescription>
            {provider.id === 'aws'
              ? 'Use read-only credentials. We validate them before scans run.'
              : 'Use OAuth with least-privilege access for deterministic cloud scanning.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {provider.id === 'aws' && (
            <>
              <div className="space-y-2">
                <Label htmlFor="aws-access-key-id">Access key ID</Label>
                <Input
                  id="aws-access-key-id"
                  placeholder="AKIA..."
                  value={form.awsAccessKeyId}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, awsAccessKeyId: event.target.value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="aws-secret-access-key">Secret access key</Label>
                <Input
                  id="aws-secret-access-key"
                  type="password"
                  placeholder="AWS secret access key"
                  value={form.awsSecretAccessKey}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, awsSecretAccessKey: event.target.value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="aws-region">Region</Label>
                <Input
                  id="aws-region"
                  placeholder="us-east-1"
                  value={form.awsRegion}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, awsRegion: event.target.value }))
                  }
                />
              </div>
            </>
          )}

          {provider.id !== 'aws' && (
            <div className="space-y-2 rounded-lg border border-muted p-3 text-sm text-muted-foreground">
              <p>
                We will request read-only permissions and automatically detect the cloud account to scan.
              </p>
              <div className="pt-1">
                <Button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isOAuthConnecting}
                >
                  {isOAuthConnecting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    `Connect ${provider.name} with OAuth`
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isSaving || isOAuthConnecting}
          >
            Cancel
          </Button>
          {provider.id === 'aws' && (
            <Button onClick={handleSubmit} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save and test'
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ScreenataIntegrationsPage() {
  const { workspaceId } = useParams({
    from: '/(protected)/workspaces/$workspaceId/screenata/integrations',
  })
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [activeProviderId, setActiveProviderId] = useState<CloudProvider>('aws')
  const [configuringProviderId, setConfiguringProviderId] = useState<CloudProvider | null>(null)
  const [runningProviderId, setRunningProviderId] = useState<CloudProvider | null>(null)
  const [connectingProviderId, setConnectingProviderId] = useState<Exclude<CloudProvider, 'aws'> | null>(null)

  const programQuery = useQuery(
    trpc.complianceProgram.getProgram.queryOptions({ framework: 'SOC2' }),
  )
  const program = programQuery.data
  const activeProvider =
    PROVIDERS.find((provider) => provider.id === activeProviderId) ?? PROVIDERS[0]

  const connectionsQuery = useQuery({
    ...trpc.integrations.getCloudConnections.queryOptions(),
    enabled: !!program?.id,
  })

  const connectionsByProvider = useMemo(() => {
    const map = new Map<CloudProvider, CloudConnection>()
    for (const connection of connectionsQuery.data ?? []) {
      map.set(connection.provider, connection as CloudConnection)
    }
    return map
  }, [connectionsQuery.data])

  const activeConnection = connectionsByProvider.get(activeProvider.id) ?? {
    provider: activeProvider.id,
    connected: false,
    integrationId: null,
    hasConfig: false,
    configSummary: {},
    lastTestedAt: null,
    lastTestStatus: null,
    lastScanAt: null,
    lastScanSummary: null,
  }

  const skipScanningMutation = useMutation(
    trpc.complianceProgram.skipScanning.mutationOptions(),
  )
  const completeIntegrationsMutation = useMutation(
    trpc.complianceProgram.completeIntegrations.mutationOptions(),
  )
  const upsertCloudConnectionMutation = useMutation(
    trpc.integrations.upsertCloudConnection.mutationOptions(),
  )
  const testCloudConnectionMutation = useMutation(
    trpc.integrations.testCloudConnection.mutationOptions(),
  )
  const runCloudScanMutation = useMutation(
    trpc.integrations.runCloudScan.mutationOptions(),
  )
  const disconnectCloudConnectionMutation = useMutation(
    trpc.integrations.disconnectCloudConnection.mutationOptions(),
  )

  const findingsQuery = useQuery({
    ...trpc.integrations.getCloudScanFindings.queryOptions({
      programId: program?.id ?? '',
      provider: activeProvider.id,
    }),
    enabled: !!program?.id && activeConnection.connected,
    refetchInterval: (query) => {
      if (runningProviderId === activeProvider.id) return 1200
      const phase = query.state.data?.phase
      if (phase === 'queued' || phase === 'clone' || phase === 'analysis') {
        return 1200
      }
      return false
    },
    refetchIntervalInBackground: true,
  })

  useEffect(() => {
    const firstConnected = PROVIDERS.find((provider) =>
      connectionsByProvider.get(provider.id)?.connected,
    )
    if (firstConnected && !connectionsByProvider.get(activeProviderId)?.connected) {
      setActiveProviderId(firstConnected.id)
    }
  }, [activeProviderId, connectionsByProvider])

  const connectProviderWithOAuth = async (provider: Exclude<CloudProvider, 'aws'>) => {
    setConnectingProviderId(provider)
    try {
      const integrationType = toIntegrationType(provider)
      const baseWebUrl = import.meta.env.VITE_WEB_URL ?? window.location.origin
      const callbackUrl = `${baseWebUrl}/auth-callback/${integrationType}`
      const initResult = await trpcClient.integrations.initCloudAuth.mutate({
        provider,
        redirectUrl: callbackUrl,
      })

      if (!initResult.authUrl) {
        throw new Error('Missing OAuth authorization URL')
      }

      const width = 820
      const height = 760
      const left = window.screenX + (window.outerWidth - width) / 2
      const top = window.screenY + (window.outerHeight - height) / 2
      const authWindow = window.open(
        initResult.authUrl,
        `${provider}-readonly-auth`,
        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,status=yes`,
      )

      if (!authWindow) {
        throw new Error('OAuth popup was blocked. Allow popups and try again.')
      }

      const expectedProvider = integrationType.toLowerCase()

      const cleanup = () => {
        window.removeEventListener('message', messageListener)
        setConnectingProviderId((current) => (current === provider ? null : current))
      }

      const messageListener = async (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return
        const eventProvider =
          typeof event.data?.provider === 'string' ? event.data.provider.toLowerCase() : ''
        if (eventProvider !== expectedProvider) return

        if (event.data?.type === 'INTEGRATION_AUTH_SUCCESS') {
          cleanup()
          authWindow.close()
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: trpc.integrations.getCloudConnections.queryKey(),
            }),
            queryClient.invalidateQueries({
              queryKey: trpc.complianceProgram.getProgram.queryKey({ framework: 'SOC2' }),
            }),
            queryClient.invalidateQueries({
              queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey({ workspaceId }),
            }),
          ])
          setActiveProviderId(provider)
          setConfiguringProviderId(null)
          toast.success(
            `${PROVIDERS.find((item) => item.id === provider)?.name ?? provider.toUpperCase()} connected`,
          )
          return
        }

        if (event.data?.type === 'INTEGRATION_AUTH_ERROR') {
          cleanup()
          authWindow.close()
          toast.error(event.data?.error || `Failed to connect ${provider.toUpperCase()}`)
        }
      }

      window.addEventListener('message', messageListener)

      const checkClosed = setInterval(() => {
        try {
          if (!authWindow.closed) return
          clearInterval(checkClosed)
          cleanup()
        } catch {
          clearInterval(checkClosed)
          cleanup()
        }
      }, 500)
    } catch (error: unknown) {
      setConnectingProviderId((current) => (current === provider ? null : current))
      toast.error(toErrorMessage(error, `Failed to connect ${provider.toUpperCase()}`))
    }
  }

  const saveAndTestConnection = async (input: CloudConnectionInput) => {
    try {
      await upsertCloudConnectionMutation.mutateAsync(input)
      const testResult = await testCloudConnectionMutation.mutateAsync({
        provider: input.provider,
      })
      if (!testResult.success) {
        toast.error(testResult.message || 'Connection test failed')
        return
      }

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: trpc.integrations.getCloudConnections.queryKey(),
        }),
        queryClient.invalidateQueries({
          queryKey: trpc.complianceProgram.getProgram.queryKey({ framework: 'SOC2' }),
        }),
      ])

      setActiveProviderId(input.provider)
      setConfiguringProviderId(null)
      toast.success(`${PROVIDERS.find((provider) => provider.id === input.provider)?.name} connected`)
    } catch (error: unknown) {
      toast.error(toErrorMessage(error, 'Failed to connect cloud provider'))
    }
  }

  const handleRunScan = (providerId: CloudProvider) => {
    if (!program?.id) return
    setActiveProviderId(providerId)
    setRunningProviderId(providerId)

    runCloudScanMutation.mutate(
      {
        programId: program.id,
        provider: providerId,
      },
      {
        onSuccess: async () => {
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: trpc.integrations.getCloudScanFindings.queryKey({
                programId: program.id,
                provider: providerId,
              }),
            }),
            queryClient.invalidateQueries({
              queryKey: trpc.integrations.getCloudConnections.queryKey(),
            }),
            queryClient.invalidateQueries({
              queryKey: trpc.complianceProgram.getProgram.queryKey({ framework: 'SOC2' }),
            }),
          ])
        },
        onError: (error: unknown) => {
          toast.error(toErrorMessage(error, 'Cloud scan failed'))
        },
        onSettled: () => {
          setRunningProviderId((current) =>
            current === providerId ? null : current,
          )
        },
      },
    )
  }

  const handleDisconnect = (providerId: CloudProvider) => {
    disconnectCloudConnectionMutation.mutate(
      { provider: providerId },
      {
        onSuccess: async () => {
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: trpc.integrations.getCloudConnections.queryKey(),
            }),
            queryClient.invalidateQueries({
              queryKey: trpc.complianceProgram.getProgram.queryKey({ framework: 'SOC2' }),
            }),
          ])
          toast.success('Provider disconnected')
        },
        onError: (error: unknown) => {
          toast.error(toErrorMessage(error, 'Failed to disconnect provider'))
        },
      },
    )
  }

  const handleSkipScanning = () => {
    if (!program?.id) return
    skipScanningMutation.mutate(
      { programId: program.id },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: trpc.complianceProgram.getProgram.queryKey({ framework: 'SOC2' }),
          })
          navigate({
            to: '/workspaces/$workspaceId/screenata/scope-confirm',
            params: { workspaceId },
          })
        },
      },
    )
  }

  const markReviewedMutation = useMutation(
    trpc.complianceProgram.markContextMemoryReviewed.mutationOptions(),
  )

  const handleContinue = () => {
    if (!program?.id) return
    completeIntegrationsMutation.mutate(
      { programId: program.id },
      {
        onSuccess: () => {
          // Auto-mark context memory as reviewed (review step removed — editing happens in connect-repo's AnalysisDrawer)
          markReviewedMutation.mutate(
            { framework: 'SOC2' },
            {
              onSuccess: () => {
                queryClient.invalidateQueries({
                  queryKey: trpc.complianceProgram.getProgram.queryKey({ framework: 'SOC2' }),
                })
                navigate({
                  to: '/workspaces/$workspaceId/screenata/scope-confirm',
                  params: { workspaceId },
                })
              },
              onError: () => {
                // Even if marking fails, still navigate forward
                queryClient.invalidateQueries({
                  queryKey: trpc.complianceProgram.getProgram.queryKey({ framework: 'SOC2' }),
                })
                navigate({
                  to: '/workspaces/$workspaceId/screenata/scope-confirm',
                  params: { workspaceId },
                })
              },
            },
          )
        },
      },
    )
  }

  const footer = (
    <>
      <button
        onClick={handleContinue}
        disabled={
          completeIntegrationsMutation.isPending ||
          runCloudScanMutation.isPending ||
          connectingProviderId !== null
        }
        className="inline-flex items-center gap-2 rounded-lg bg-foreground px-5 py-2.5 text-sm font-medium text-background transition-opacity hover:opacity-90 disabled:opacity-50"
      >
        {completeIntegrationsMutation.isPending ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Continuing...
          </>
        ) : (
          <>
            Continue
            <ArrowRight className="h-4 w-4" />
          </>
        )}
      </button>
      <button
        onClick={handleSkipScanning}
        disabled={
          skipScanningMutation.isPending ||
          completeIntegrationsMutation.isPending ||
          runCloudScanMutation.isPending ||
          connectingProviderId !== null
        }
        className="text-sm text-muted-foreground underline-offset-2 hover:underline"
      >
        {skipScanningMutation.isPending ? 'Skipping...' : 'Skip'}
      </button>
    </>
  )

  return (
    <ScreenataStepLayout
      section="Infrastructure"
      title="Infrastructure Integrations"
      description="Connect your infrastructure to automatically collect compliance evidence."
      footer={footer}
      onBack={() =>
        navigate({
          to: '/workspaces/$workspaceId/screenata/connect-repo',
          params: { workspaceId },
        })
      }
      onNext={handleContinue}
      disableNext={
        skipScanningMutation.isPending ||
        completeIntegrationsMutation.isPending ||
        runCloudScanMutation.isPending ||
        connectingProviderId !== null
      }
    >
      {programQuery.isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="space-y-3">
          {PROVIDERS.map((provider) => {
            const connection = connectionsByProvider.get(provider.id) ?? null
            const connected = !!connection?.connected
            const isActive = activeProvider.id === provider.id
            const providerSummary = asSummary(connection?.lastScanSummary ?? null)
            const isRunningThisProvider = runningProviderId === provider.id
            const isOauthProvider = provider.id === 'gcp' || provider.id === 'azure'
            const isConnectingThisProvider = connectingProviderId === provider.id

            return (
              <div
                key={provider.id}
                className={`w-full rounded-xl border-2 px-5 py-4 text-left transition-all ${
                  isActive
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50 hover:bg-muted/40'
                }`}
                onClick={() => setActiveProviderId(provider.id)}
              >
                <div className="flex items-center gap-4">
                  <provider.icon className="w-8 h-8 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{provider.name}</span>
                      {connected ? (
                        <span className="flex items-center gap-1 text-xs text-emerald-600">
                          <CheckCircle2 className="w-3 h-3" />
                          Connected
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Circle className="w-3 h-3" />
                          Not connected
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      {provider.description}
                    </p>
                    {connected && providerSummary && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        Last scan: {providerSummary.pass} pass, {providerSummary.warn} warn, {providerSummary.fail} fail, {providerSummary.error} error
                      </p>
                    )}
                    {connected && connection?.lastScanAt && (
                      <p className="text-xs text-muted-foreground">
                        Last scanned: {formatDate(connection.lastScanAt)}
                      </p>
                    )}
                  </div>

                  <div className="flex shrink-0 items-center gap-2">
                    {connected ? (
                      <>
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation()
                            handleRunScan(provider.id)
                          }}
                          disabled={
                            runCloudScanMutation.isPending ||
                            disconnectCloudConnectionMutation.isPending ||
                            connectingProviderId !== null
                          }
                          className="inline-flex items-center gap-1.5 rounded-lg bg-foreground px-3 py-2 text-xs font-medium text-background transition-opacity hover:opacity-90 disabled:opacity-50"
                        >
                          {isRunningThisProvider ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Play className="h-3.5 w-3.5" />
                          )}
                          {isRunningThisProvider ? 'Scanning...' : 'Run scan'}
                        </button>
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation()
                            if (provider.id === 'aws') {
                              setConfiguringProviderId(provider.id)
                              return
                            }
                            connectProviderWithOAuth(provider.id)
                          }}
                          disabled={runCloudScanMutation.isPending || isConnectingThisProvider}
                          className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
                        >
                          {isConnectingThisProvider ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <RefreshCcw className="h-3.5 w-3.5" />
                          )}
                          {isConnectingThisProvider ? 'Connecting...' : (isOauthProvider ? 'Reconnect (OAuth)' : 'Reconnect')}
                        </button>
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation()
                            handleDisconnect(provider.id)
                          }}
                          disabled={
                            disconnectCloudConnectionMutation.isPending ||
                            runCloudScanMutation.isPending ||
                            isConnectingThisProvider
                          }
                          className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs text-muted-foreground hover:text-red-600 disabled:opacity-50"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          Disconnect
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation()
                          if (provider.id === 'aws') {
                            setConfiguringProviderId(provider.id)
                            return
                          }
                          connectProviderWithOAuth(provider.id)
                        }}
                        disabled={isConnectingThisProvider}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-foreground px-3 py-2 text-xs font-medium text-background transition-opacity hover:opacity-90"
                      >
                        {isConnectingThisProvider ? (
                          <>
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            Connecting...
                          </>
                        ) : (
                          'Connect'
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )
          })}

          {activeConnection.connected && (
            <div className="mt-4 space-y-3 rounded-xl border bg-muted/10 p-3">
              <p className="text-sm font-medium">
                {activeProvider.name} scan results
              </p>
              <ScanProgressBar
                percent={
                  findingsQuery.data?.percent ??
                  (runningProviderId === activeProvider.id ? 12 : 0)
                }
              />
              <div className="max-h-[260px] overflow-y-auto rounded-lg border bg-background/70 p-2.5">
                <ScanFindingsFeed
                  findings={findingsQuery.data?.findings ?? []}
                  fallbackMessage={
                    runningProviderId === activeProvider.id
                      ? `Starting ${activeProvider.name} scan...`
                      : `Run ${activeProvider.name} scan to collect infrastructure evidence.`
                  }
                />
              </div>
              <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                {activeConnection.lastTestStatus && (
                  <span>
                    Credential test: {activeConnection.lastTestStatus.toUpperCase()}
                    {activeConnection.lastTestedAt ? ` at ${formatDate(activeConnection.lastTestedAt)}` : ''}
                  </span>
                )}
                {activeConnection.lastScanSummary && asSummary(activeConnection.lastScanSummary) && (
                  <span>
                    Latest summary: {asSummary(activeConnection.lastScanSummary)?.pass} pass, {asSummary(activeConnection.lastScanSummary)?.warn} warn, {asSummary(activeConnection.lastScanSummary)?.fail} fail, {asSummary(activeConnection.lastScanSummary)?.error} error
                  </span>
                )}
              </div>
            </div>
          )}

          <CloudConnectionDialog
            provider={
              configuringProviderId === 'aws'
                ? (PROVIDERS.find((provider) => provider.id === 'aws') ?? null)
                : null
            }
            open={configuringProviderId === 'aws'}
            onOpenChange={(open) => {
              if (!open) setConfiguringProviderId(null)
            }}
            isSaving={upsertCloudConnectionMutation.isPending || testCloudConnectionMutation.isPending}
            isOAuthConnecting={connectingProviderId !== null}
            initialSummary={
              configuringProviderId
                ? connectionsByProvider.get(configuringProviderId)?.configSummary ?? {}
                : {}
            }
            onSave={saveAndTestConnection}
            onConnectOAuth={connectProviderWithOAuth}
          />
        </div>
      )}
    </ScreenataStepLayout>
  )
}
